// Wave 3: per-tile drilldown queries for the Executive Dashboard.
// Each function returns detail rows from the transactional source — the same tables the
// aggregate tiles read from — so the row totals reconcile to the tile value exactly.

import { supabase } from '../supabase';
import { fetchAllChunked } from './pagination';
import i18n from '../../i18n';

export interface DrilldownResult {
  columns: Array<{ key: string; label: string; format?: 'currency' | 'number' | 'date' | 'text' }>;
  rows: Array<Record<string, unknown>>;
  total?: number;
  totalLabel?: string;
}

function co<T>(builder: T, companyId?: string | null): T {
  return companyId ? (builder as unknown as { eq: (c: string, v: string) => T }).eq('company_id', companyId) : builder;
}

const monthStartIso = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();

// 1. Sales Pipeline — active opportunities
export async function drillSalesPipeline(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('opportunities')
      .select('id, title, stage, estimated_value, expected_close_date, customer:customers!customer_id(name)')
      .not('stage', 'in', '("won","lost","closed_won","closed_lost")'),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((o: { id: string; title: string; stage: string; estimated_value: number | null; expected_close_date: string | null; customer: { name: string } | null }) => ({
    title: o.title,
    customer: o.customer?.name ?? '—',
    stage: o.stage,
    estimated_value: Number(o.estimated_value ?? 0),
    expected_close_date: o.expected_close_date,
  }));
  return {
    columns: [
      { key: 'title', label: i18n.t('drilldown.col_opportunity', 'Opportunity') },
      { key: 'customer', label: i18n.t('drilldown.col_customer', 'Customer') },
      { key: 'stage', label: i18n.t('drilldown.col_stage', 'Stage') },
      { key: 'estimated_value', label: i18n.t('drilldown.col_value', 'Value'), format: 'currency' },
      { key: 'expected_close_date', label: i18n.t('drilldown.col_expected_close', 'Expected Close'), format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.estimated_value as number), 0),
    totalLabel: i18n.t('drilldown.total_pipeline_total', 'Pipeline total'),
  };
}

// 2. Won Deals MTD
export async function drillWonDealsMtd(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('quotations')
      .select('id, quotation_number, total, updated_at, customer:customers!customer_id(name)')
      .eq('status', 'won')
      .gte('updated_at', monthStartIso()),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((q: { quotation_number: string; total: number; updated_at: string; customer: { name: string } | null }) => ({
    quotation_number: q.quotation_number,
    customer: q.customer?.name ?? '—',
    total: Number(q.total ?? 0),
    updated_at: q.updated_at,
  }));
  return {
    columns: [
      { key: 'quotation_number', label: i18n.t('drilldown.col_quotation', 'Quotation') },
      { key: 'customer', label: i18n.t('drilldown.col_customer', 'Customer') },
      { key: 'total', label: i18n.t('drilldown.col_value', 'Value'), format: 'currency' },
      { key: 'updated_at', label: i18n.t('drilldown.col_won_on', 'Won On'), format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: i18n.t('drilldown.total_total_won_mtd', 'Total won MTD'),
  };
}

// 3. Outstanding AR
export async function drillOutstandingAR(companyId?: string | null): Promise<DrilldownResult> {
  // Total must reconcile to the tile exactly: chunked full fetch (PostgREST
  // db-max-rows silently caps single responses at 1000, even with .limit(2000)).
  type ARRow = { id: string; invoice_number: string; total: number; paid_amount: number | null; due_date: string; status: string; customer: { name: string } | null };
  const data = await fetchAllChunked<ARRow>((cursor, chunk) => {
    let q = co(
      supabase
        .from('invoices')
        .select('id, invoice_number, total, paid_amount, due_date, status, customer:customers!customer_id(name)')
        .in('status', ['issued', 'partial', 'overdue'])
        .is('deleted_at', null)
        .order('id', { ascending: true }).limit(chunk),
      companyId,
    );
    if (cursor) q = q.gt('id', cursor);
    return q as unknown as PromiseLike<{ data: ARRow[] | null; error: { message: string } | null }>;
  });
  data.sort((a, b) => String(a.due_date ?? '').localeCompare(String(b.due_date ?? '')));
  const rows = data.map((i) => ({
    invoice_number: i.invoice_number,
    customer: i.customer?.name ?? '—',
    due_date: i.due_date,
    status: i.status,
    outstanding: Number(i.total ?? 0) - Number(i.paid_amount ?? 0),
  }));
  return {
    columns: [
      { key: 'invoice_number', label: i18n.t('drilldown.col_invoice', 'Invoice') },
      { key: 'customer', label: i18n.t('drilldown.col_customer', 'Customer') },
      { key: 'due_date', label: i18n.t('drilldown.col_due', 'Due'), format: 'date' },
      { key: 'status', label: i18n.t('drilldown.col_status', 'Status') },
      { key: 'outstanding', label: i18n.t('drilldown.col_outstanding', 'Outstanding'), format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.outstanding as number), 0),
    totalLabel: i18n.t('drilldown.total_total_outstanding_ar', 'Total outstanding AR'),
  };
}

// 4. Outstanding AP
export async function drillOutstandingAP(companyId?: string | null): Promise<DrilldownResult> {
  // Same contract as AR: the reduced total is a finance figure — chunked full
  // fetch (this query had no explicit limit and was silently capped at 1000).
  type APRow = { id: string; purchase_invoice_number: string; total: number; paid_amount: number | null; due_date: string; status: string; supplier: { name: string } | null };
  const data = await fetchAllChunked<APRow>((cursor, chunk) => {
    let q = co(
      supabase
        .from('supplier_invoices')
        .select('id, purchase_invoice_number, total, paid_amount, due_date, status, supplier:suppliers!supplier_id(name)')
        .in('status', ['received', 'approved', 'matched'])
        .order('id', { ascending: true }).limit(chunk),
      companyId,
    );
    if (cursor) q = q.gt('id', cursor);
    return q as unknown as PromiseLike<{ data: APRow[] | null; error: { message: string } | null }>;
  });
  data.sort((a, b) => String(a.due_date ?? '').localeCompare(String(b.due_date ?? '')));
  const rows = data.map((i) => ({
    purchase_invoice_number: i.purchase_invoice_number,
    supplier: i.supplier?.name ?? '—',
    due_date: i.due_date,
    status: i.status,
    outstanding: Number(i.total ?? 0) - Number(i.paid_amount ?? 0),
  }));
  return {
    columns: [
      { key: 'purchase_invoice_number', label: i18n.t('drilldown.col_supplier_invoice', 'Supplier Invoice') },
      { key: 'supplier', label: i18n.t('drilldown.col_supplier', 'Supplier') },
      { key: 'due_date', label: i18n.t('drilldown.col_due', 'Due'), format: 'date' },
      { key: 'status', label: i18n.t('drilldown.col_status', 'Status') },
      { key: 'outstanding', label: i18n.t('drilldown.col_outstanding', 'Outstanding'), format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.outstanding as number), 0),
    totalLabel: i18n.t('drilldown.total_total_outstanding_ap', 'Total outstanding AP'),
  };
}

// 5. Cash Position
export async function drillCashPosition(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('treasury_accounts').select('id, account_name, bank_name, current_balance, currency'),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((a: { account_name: string; bank_name: string | null; current_balance: number; currency: string | null }) => ({
    account_name: a.account_name,
    bank_name: a.bank_name ?? '—',
    currency: a.currency ?? 'SAR',
    current_balance: Number(a.current_balance ?? 0),
  }));
  return {
    columns: [
      { key: 'account_name', label: i18n.t('drilldown.col_account', 'Account') },
      { key: 'bank_name', label: i18n.t('drilldown.col_bank', 'Bank') },
      { key: 'currency', label: i18n.t('drilldown.col_ccy', 'CCY') },
      { key: 'current_balance', label: i18n.t('drilldown.col_balance', 'Balance'), format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.current_balance as number), 0),
    totalLabel: i18n.t('drilldown.total_total_cash_all_currencies', 'Total cash (all currencies)'),
  };
}

// 6. Active Projects
export async function drillActiveProjects(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('projects')
      .select('id, name, status, start_date, end_date, manager:profiles!manager_id(full_name)')
      .eq('status', 'active')
      // List-only drill (no reduced total): 1000 is the true server cap.
      .order('start_date', { ascending: false }).limit(1000),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((p: { name: string; status: string; start_date: string | null; end_date: string | null; manager: { full_name: string } | null }) => ({
    name: p.name,
    manager: p.manager?.full_name ?? '—',
    start_date: p.start_date,
    end_date: p.end_date,
  }));
  return {
    columns: [
      { key: 'name', label: i18n.t('drilldown.col_project', 'Project') },
      { key: 'manager', label: i18n.t('drilldown.col_manager', 'Manager') },
      { key: 'start_date', label: i18n.t('drilldown.col_start', 'Start'), format: 'date' },
      { key: 'end_date', label: i18n.t('drilldown.col_end', 'End'), format: 'date' },
    ],
    rows,
  };
}

// 7. Headcount
export async function drillHeadcount(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('employees')
      .select('id, full_name_en, job_title_en, department:departments!department_id(name_en)')
      .eq('status', 'active')
      // List-only drill (no reduced total): 1000 is the true server cap.
      .order('full_name_en').limit(1000),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((e: { full_name_en: string | null; job_title_en: string | null; department: { name_en: string } | null }) => ({
    full_name: e.full_name_en ?? '—',
    job_title: e.job_title_en ?? '—',
    department: e.department?.name_en ?? '—',
  }));
  return {
    columns: [
      { key: 'full_name', label: i18n.t('drilldown.col_employee', 'Employee') },
      { key: 'job_title', label: i18n.t('drilldown.col_title', 'Title') },
      { key: 'department', label: i18n.t('drilldown.col_department', 'Department') },
    ],
    rows,
  };
}

// 8. Open NCRs
export async function drillOpenNcrs(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('ncr_reports')
      .select('id, ncr_number, title, severity, status, created_at')
      .not('status', 'in', '("closed","rejected")')
      // List-only drill (no reduced total): 1000 is the true server cap.
      .order('created_at', { ascending: false }).limit(1000),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((n: { ncr_number: string; title: string; severity: string | null; status: string; created_at: string }) => ({
    ncr_number: n.ncr_number,
    title: n.title,
    severity: n.severity ?? '—',
    status: n.status,
    created_at: n.created_at,
  }));
  return {
    columns: [
      { key: 'ncr_number', label: i18n.t('drilldown.col_ncr', 'NCR') },
      { key: 'title', label: i18n.t('drilldown.col_title', 'Title') },
      { key: 'severity', label: i18n.t('drilldown.col_severity', 'Severity') },
      { key: 'status', label: i18n.t('drilldown.col_status', 'Status') },
      { key: 'created_at', label: i18n.t('drilldown.col_opened', 'Opened'), format: 'date' },
    ],
    rows,
  };
}

export type DrilldownKey =
  | 'sales_pipeline'
  | 'won_deals_mtd'
  | 'outstanding_ar'
  | 'outstanding_ap'
  | 'cash_position'
  | 'active_projects'
  | 'headcount'
  | 'open_ncrs';

export const DRILLDOWN_LOADERS: Record<DrilldownKey, (companyId?: string | null) => Promise<DrilldownResult>> = {
  sales_pipeline: drillSalesPipeline,
  won_deals_mtd: drillWonDealsMtd,
  outstanding_ar: drillOutstandingAR,
  outstanding_ap: drillOutstandingAP,
  cash_position: drillCashPosition,
  active_projects: drillActiveProjects,
  headcount: drillHeadcount,
  open_ncrs: drillOpenNcrs,
};

export const DRILLDOWN_TITLES: Record<DrilldownKey, string> = {
  sales_pipeline: 'Sales Pipeline — detail',
  won_deals_mtd: 'Won Deals (MTD) — detail',
  outstanding_ar: 'Outstanding AR — detail',
  outstanding_ap: 'Outstanding AP — detail',
  cash_position: 'Cash Position — detail',
  active_projects: 'Active Projects — detail',
  headcount: 'Headcount — detail',
  open_ncrs: 'Open NCRs — detail',
};
